/** Pure color conversions used by the custom ColorPicker. No DOM, no React.
 *  All inputs/outputs use the conventions:
 *    - hue h     ∈ [0, 360)
 *    - saturation/value/lightness/alpha s, v, l, a ∈ [0, 1]
 *    - rgb r, g, b ∈ [0, 255] (integers in serialised forms)
 *    - hex always starts with `#`; lowercase; 3/6/8 hex digits accepted
 *      on read, written as 6 (rgb) or 8 (rgba when alpha < 1). */

export interface HSV {
  h: number;
  s: number;
  v: number;
  a: number;
}

export interface HSL {
  h: number;
  s: number;
  l: number;
  a: number;
}

export interface RGB {
  r: number;
  g: number;
  b: number;
  a: number;
}

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));
const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

/** Wraps `h` into [0, 360). Negative inputs cycle around so callers can
 *  freely subtract from a slider position without sign-checking. */
export function wrapHue(h: number): number {
  const m = h % 360;
  return m < 0 ? m + 360 : m;
}

export function hsv2rgb(hsv: HSV): RGB {
  const h = wrapHue(hsv.h);
  const s = clamp01(hsv.s);
  const v = clamp01(hsv.v);
  const c = v * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) {
    r1 = c;
    g1 = x;
  } else if (hp < 2) {
    r1 = x;
    g1 = c;
  } else if (hp < 3) {
    g1 = c;
    b1 = x;
  } else if (hp < 4) {
    g1 = x;
    b1 = c;
  } else if (hp < 5) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }
  const m = v - c;
  return {
    r: clamp255((r1 + m) * 255),
    g: clamp255((g1 + m) * 255),
    b: clamp255((b1 + m) * 255),
    a: clamp01(hsv.a),
  };
}

export function rgb2hsv(rgb: RGB): HSV {
  const r = clamp255(rgb.r) / 255;
  const g = clamp255(rgb.g) / 255;
  const b = clamp255(rgb.b) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h, s, v, a: clamp01(rgb.a) };
}

function pad2(n: number): string {
  const s = n.toString(16);
  return s.length === 1 ? `0${s}` : s;
}

/** Always emits 6-digit hex; appends 2-digit alpha only when a < 1. */
export function rgb2hex(rgb: RGB): string {
  const base = `#${pad2(clamp255(rgb.r))}${pad2(clamp255(rgb.g))}${pad2(clamp255(rgb.b))}`;
  if (rgb.a >= 0.9961) return base;
  const a = clamp255(rgb.a * 255);
  return `${base}${pad2(a)}`;
}

/** Forgiving parser that accepts every CSS-color form a controlled
 *  caller might hand us:
 *    - hex (3/6/8 digits, with or without leading `#`)
 *    - `rgb(r, g, b)` / `rgba(r, g, b, a)` — the form browsers
 *      normalise inline `style.color` to when reading it back via
 *      `el.style.color` after the editor wraps a selection. Without
 *      this branch the ColorPicker's controlled-mode `useEffect`
 *      treats the post-apply rgb readback as "unparsable" and falls
 *      through to BLACK HSV — making the popup's preview flick to
 *      black for one render between every swatch click.
 *    - CSS variables / named colours via a one-shot DOM probe (we
 *      only reach this branch when the simpler regex routes failed,
 *      so the cost is paid lazily). Returns null when the browser
 *      itself can't resolve the input — that's the signal callers
 *      use to skip a no-op `setHsv` rather than to paint black.
 *
 *  Naming kept as `hex2rgb` for backwards compatibility with the
 *  many call sites in `ColorPicker.tsx`; the function is now a
 *  full CSS-color parser, not just a hex parser. */
export function hex2rgb(input: string, resolveScope?: Element | null): RGB | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  // Route 1 — pure hex (#abc / #abcdef / #abcdefff / abcdef without #)
  const hexBody = trimmed.toLowerCase().replace(/^#/, '');
  if (/^[0-9a-f]+$/.test(hexBody)) {
    const hexParsed = parseHexBody(hexBody);
    if (hexParsed !== null) return hexParsed;
  }

  // Route 2 — rgb() / rgba() functional notation. Browsers normalise
  // every readback of `el.style.color` to this form; the ColorPicker's
  // controlled-mode useEffect must round-trip through it without
  // collapsing to black.
  const fnMatch = /^rgba?\(\s*([^)]+)\)$/i.exec(trimmed);
  if (fnMatch !== null) {
    const inner = fnMatch[1];
    if (inner !== undefined) {
      const fromFn = parseRgbFunction(inner);
      if (fromFn !== null) return fromFn;
    }
  }

  // Route 3 — let the browser resolve CSS variables, named colours
  // (`tomato`, `transparent`, `currentColor`, …) and modern colour
  // syntax via a hidden probe. `getComputedStyle` always serialises
  // the resolved value as `rgb(...)` / `rgba(...)`, so we can re-run
  // the rgb-function parser on its output.
  if (typeof document !== 'undefined' && typeof getComputedStyle === 'function') {
    const probe = document.createElement('span');
    probe.style.color = '';
    probe.style.color = trimmed;
    if (probe.style.color === '') return null; // browser rejected the input
    const scope =
      resolveScope instanceof HTMLElement && resolveScope.isConnected
        ? resolveScope
        : document.body;
    scope.appendChild(probe);
    try {
      const computed = getComputedStyle(probe).color;
      const m = /^rgba?\(\s*([^)]+)\)$/i.exec(computed);
      if (m !== null) {
        const inner = m[1];
        if (inner !== undefined) {
          return parseRgbFunction(inner);
        }
      }
    } finally {
      probe.remove();
    }
  }

  return null;
}

function parseHexBody(s: string): RGB | null {
  let r: number;
  let g: number;
  let b: number;
  let a = 1;
  if (s.length === 3) {
    // Expand 3-digit shorthand by duplicating each nibble — the regex
    // above already ensured every char is a valid hex digit, so the
    // substring extractions below cannot produce NaN.
    const expanded = s.replace(/(.)/g, '$1$1');
    r = parseInt(expanded.slice(0, 2), 16);
    g = parseInt(expanded.slice(2, 4), 16);
    b = parseInt(expanded.slice(4, 6), 16);
  } else if (s.length === 4) {
    const expanded = s.replace(/(.)/g, '$1$1');
    r = parseInt(expanded.slice(0, 2), 16);
    g = parseInt(expanded.slice(2, 4), 16);
    b = parseInt(expanded.slice(4, 6), 16);
    a = parseInt(expanded.slice(6, 8), 16) / 255;
  } else if (s.length === 6) {
    r = parseInt(s.slice(0, 2), 16);
    g = parseInt(s.slice(2, 4), 16);
    b = parseInt(s.slice(4, 6), 16);
  } else if (s.length === 8) {
    r = parseInt(s.slice(0, 2), 16);
    g = parseInt(s.slice(2, 4), 16);
    b = parseInt(s.slice(4, 6), 16);
    a = parseInt(s.slice(6, 8), 16) / 255;
  } else {
    return null;
  }
  return { r, g, b, a };
}

function parseRgbFunction(inner: string): RGB | null {
  // Accept any CSS rgb() syntax: comma-separated `rgb(255, 0, 0)`,
  // space-separated `rgb(255 0 0)`, with optional `/ <alpha>` separator
  // (modern syntax). Percentage components (`50%`) are also resolved.
  const parts = inner.split(/[\s,/]+/).filter((p) => p !== '');
  if (parts.length !== 3 && parts.length !== 4) return null;
  const channel = (raw: string): number | null => {
    if (raw.endsWith('%')) {
      const pct = parseFloat(raw.slice(0, -1));
      if (!Number.isFinite(pct)) return null;
      return (pct / 100) * 255;
    }
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  };
  const alphaChannel = (raw: string): number | null => {
    if (raw.endsWith('%')) {
      const pct = parseFloat(raw.slice(0, -1));
      if (!Number.isFinite(pct)) return null;
      return Math.max(0, Math.min(1, pct / 100));
    }
    const n = parseFloat(raw);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : null;
  };
  const r = channel(parts[0] ?? '');
  const g = channel(parts[1] ?? '');
  const b = channel(parts[2] ?? '');
  if (r === null || g === null || b === null) return null;
  let a = 1;
  if (parts.length === 4) {
    const parsed = alphaChannel(parts[3] ?? '');
    if (parsed === null) return null;
    a = parsed;
  }
  return { r, g, b, a };
}

/** Rec. 709 luma — used by the swatch grid to flip the checkmark glyph
 *  to dark on pale colours. Returns true if the colour is "light". */
export function isLightRgb(rgb: RGB): boolean {
  return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b > 150;
}

/** CSS string useful for the live preview chip — emits rgba() so the
 *  alpha shows through on the checkerboard underneath. */
export function rgb2css(rgb: RGB): string {
  const r = clamp255(rgb.r);
  const g = clamp255(rgb.g);
  const b = clamp255(rgb.b);
  const a = clamp01(rgb.a);
  if (a >= 0.9961) return `rgb(${String(r)}, ${String(g)}, ${String(b)})`;
  return `rgb(${String(r)} ${String(g)} ${String(b)} / ${a.toFixed(3)})`;
}

/** Useful for "the saturation/value plane behind the dot" — emits the
 *  pure hue at full S+V so the gradient can multiply over a white→black
 *  layered overlay. */
export function pureHueCss(h: number): string {
  const rgb = hsv2rgb({ h: wrapHue(h), s: 1, v: 1, a: 1 });
  return rgb2css(rgb);
}

/** HSV → HSL — same hue, but saturation/lightness use the "double cone"
 *  convention. Useful when users prefer to think in HSL (the canonical
 *  CSS3 colour space). */
export function hsv2hsl(hsv: HSV): HSL {
  const v = clamp01(hsv.v);
  const s = clamp01(hsv.s);
  const l = v * (1 - s / 2);
  const sl = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);
  return { h: wrapHue(hsv.h), s: sl, l, a: clamp01(hsv.a) };
}

/** HSL → HSV — inverse of {@link hsv2hsl}. */
export function hsl2hsv(hsl: HSL): HSV {
  const l = clamp01(hsl.l);
  const s = clamp01(hsl.s);
  const v = l + s * Math.min(l, 1 - l);
  const sv = v === 0 ? 0 : 2 * (1 - l / v);
  return { h: wrapHue(hsl.h), s: sv, v, a: clamp01(hsl.a) };
}

/** Format the HSL components for display. Hue is rounded to int degrees,
 *  saturation/lightness to int percent, alpha to 2 decimals. */
export function formatHsl(hsl: HSL, alpha: boolean): string {
  const h = Math.round(wrapHue(hsl.h));
  const s = Math.round(clamp01(hsl.s) * 100);
  const l = Math.round(clamp01(hsl.l) * 100);
  if (!alpha || hsl.a >= 0.9961) return `hsl(${String(h)}, ${String(s)}%, ${String(l)}%)`;
  return `hsla(${String(h)}, ${String(s)}%, ${String(l)}%, ${hsl.a.toFixed(2)})`;
}
